import {
  PortalItineraryStatus,
  ScheduleChangeSeverity,
} from "@b2bportal/air-booking-api";
import { ProductType } from "@b2bportal/core-types";
import { ReservationStateEnum as GroundReservationStateEnum } from "@b2bportal/ground-api";
import { ReservationStatusEnum as HomesReservationStatusEnum } from "@b2bportal/homes-api";
import { ReservationStateEnum as LodgingReservationStateEnum } from "@b2bportal/lodging-api";
import { ActivityBookingStatus } from "@b2bportal/rbc-activities-api";
import { PackageReservationState } from "@b2bportal/rbc-bundled-packages-api";
import { PillTone } from "@tenant/ui";
import type { TFunction } from "i18next";
import type { StatusMapEntry } from "./types";

const COMPLETED_KEY = "Completed";

interface ProductStatusConfig<T extends string> {
  buildStatusMap: (t: TFunction) => Record<T, StatusMapEntry>;
  defaultKey: string;
  terminalStatuses: readonly string[];
  customResolver?: (options: {
    status: string;
    isPastTrip: boolean;
    scheduleChangeSeverity?: ScheduleChangeSeverity;
    t: TFunction;
  }) => StatusMapEntry | null;
}

const carsConfig: ProductStatusConfig<GroundReservationStateEnum> = {
  buildStatusMap: (t) => ({
    [GroundReservationStateEnum.Failure]: {
      tone: PillTone.error,
      label: t("problem"),
    },
    [GroundReservationStateEnum.Cancelled]: {
      tone: PillTone.error,
      label: t("cancelled"),
    },
    [GroundReservationStateEnum.Confirmed]: {
      tone: PillTone.success,
      label: t("confirmed"),
    },
    [GroundReservationStateEnum.Pending]: {
      tone: PillTone.neutral,
      label: t("pending"),
    },
    [COMPLETED_KEY]: {
      tone: PillTone.neutral,
      label: t("apac.trips.completed"),
    },
  }),
  defaultKey: GroundReservationStateEnum.Pending,
  terminalStatuses: [
    GroundReservationStateEnum.Cancelled,
    GroundReservationStateEnum.Failure,
  ],
};

const activitiesConfig: ProductStatusConfig<ActivityBookingStatus> = {
  buildStatusMap: (t) => ({
    [ActivityBookingStatus.Cancelled]: {
      tone: PillTone.error,
      label: t("activities.postBooking.status.cancelled"),
    },
    [ActivityBookingStatus.Pending]: {
      tone: PillTone.caution,
      label: t("activities.postBooking.status.pending"),
    },
    [ActivityBookingStatus.Confirmed]: {
      tone: PillTone.success,
      label: t("activities.postBooking.status.confirmed"),
    },
    [ActivityBookingStatus.Unknown]: {
      tone: PillTone.neutral,
      label: t("activities.postBooking.status.unknown"),
    },
    [COMPLETED_KEY]: {
      tone: PillTone.neutral,
      label: t("apac.trips.completed"),
    },
  }),
  defaultKey: ActivityBookingStatus.Unknown,
  terminalStatuses: [ActivityBookingStatus.Cancelled],
};

const lodgingConfig: ProductStatusConfig<LodgingReservationStateEnum> = {
  buildStatusMap: (t) => ({
    [LodgingReservationStateEnum.CancellationFailure]: {
      tone: PillTone.error,
      label: t("core-hotels.reservationState.CancellationFailure"),
    },
    [LodgingReservationStateEnum.Cancelled]: {
      tone: PillTone.error,
      label: t("core-hotels.reservationState.Cancelled"),
    },
    [LodgingReservationStateEnum.Confirmed]: {
      tone: PillTone.success,
      label: t("core-hotels.reservationState.Confirmed"),
    },
    [LodgingReservationStateEnum.Pending]: {
      tone: PillTone.neutral,
      label: t("core-hotels.reservationState.Pending"),
    },
    [COMPLETED_KEY]: {
      tone: PillTone.neutral,
      label: t("apac.trips.completed"),
    },
  }),
  defaultKey: LodgingReservationStateEnum.Pending,
  terminalStatuses: [
    LodgingReservationStateEnum.Cancelled,
    LodgingReservationStateEnum.CancellationFailure,
  ],
};

const flightsConfig: ProductStatusConfig<PortalItineraryStatus> = {
  buildStatusMap: (t) => ({
    [PortalItineraryStatus.Modified]: {
      tone: PillTone.caution,
      label: t("core-flights.itinerary.status.modified"),
    },
    [PortalItineraryStatus.Canceled]: {
      tone: PillTone.error,
      label: t("core-flights.itinerary.status.canceled"),
    },
    [PortalItineraryStatus.Confirmed]: {
      tone: PillTone.success,
      label: t("core-flights.itinerary.status.confirmed"),
    },
    [PortalItineraryStatus.Pending]: {
      tone: PillTone.neutral,
      label: t("core-flights.itinerary.status.pending"),
    },
    [COMPLETED_KEY]: {
      tone: PillTone.neutral,
      label: t("apac.trips.completed"),
    },
  }),
  defaultKey: PortalItineraryStatus.Pending,
  terminalStatuses: [PortalItineraryStatus.Canceled],
  customResolver: ({ status, scheduleChangeSeverity, t }) => {
    // Handle schedule changes
    if (
      status === PortalItineraryStatus.Modified &&
      scheduleChangeSeverity != null &&
      scheduleChangeSeverity !== ScheduleChangeSeverity.Unknown
    ) {
      if (scheduleChangeSeverity === ScheduleChangeSeverity.Major) {
        return {
          tone: PillTone.error,
          label: t("core-flights.itinerary.status.majorScheduleChange"),
        };
      }
      if (scheduleChangeSeverity === ScheduleChangeSeverity.Minor) {
        return {
          tone: PillTone.caution,
          label: t("core-flights.itinerary.status.minorScheduleChange"),
        };
      }
    }
    return null;
  },
};

const packagesConfig: ProductStatusConfig<PackageReservationState> = {
  buildStatusMap: (t) => ({
    [PackageReservationState.Cancelled]: {
      tone: PillTone.error,
      label: t("cancelled"),
    },
    [PackageReservationState.CancellationFailure]: {
      tone: PillTone.error,
      label: t("problem"),
    },
    [PackageReservationState.Confirmed]: {
      tone: PillTone.success,
      label: t("confirmed"),
    },
    [PackageReservationState.Pending]: {
      tone: PillTone.neutral,
      label: t("pending"),
    },
    [PackageReservationState.PartiallyCancelled]: {
      tone: PillTone.caution,
      label: t("core-packages.itinerary.status.partiallyCancelled"),
    },
    [PackageReservationState.ReservationStateFailure]: {
      tone: PillTone.error,
      label: t("problem"),
    },
    [COMPLETED_KEY]: {
      tone: PillTone.neutral,
      label: t("apac.trips.completed"),
    },
  }),
  defaultKey: PackageReservationState.Pending,
  terminalStatuses: [
    PackageReservationState.Cancelled,
    PackageReservationState.PartiallyCancelled,
    PackageReservationState.CancellationFailure,
    PackageReservationState.ReservationStateFailure,
  ],
};

const homesConfig: ProductStatusConfig<HomesReservationStatusEnum> = {
  buildStatusMap: (t) => ({
    [HomesReservationStatusEnum.Cancelled]: {
      tone: PillTone.error,
      label: t("cancelled"),
    },
    [HomesReservationStatusEnum.Confirmed]: {
      tone: PillTone.success,
      label: t("confirmed"),
    },
    [HomesReservationStatusEnum.Unknown]: {
      tone: PillTone.neutral,
      label: t("unknown"),
    },
    [COMPLETED_KEY]: {
      tone: PillTone.neutral,
      label: t("apac.trips.completed"),
    },
  }),
  defaultKey: HomesReservationStatusEnum.Unknown,
  terminalStatuses: [HomesReservationStatusEnum.Cancelled],
};

type ProductStatusConfigMap = {
  [ProductType.CARS]: ProductStatusConfig<GroundReservationStateEnum>;
  [ProductType.ACTIVITIES]: ProductStatusConfig<ActivityBookingStatus>;
  [ProductType.LODGING]: ProductStatusConfig<LodgingReservationStateEnum>;
  [ProductType.FLIGHTS]: ProductStatusConfig<PortalItineraryStatus>;
  [ProductType.PACKAGE]: ProductStatusConfig<PackageReservationState>;
  [ProductType.HOMES]: ProductStatusConfig<HomesReservationStatusEnum>;
};

export const PRODUCT_STATUS_CONFIGS: Partial<ProductStatusConfigMap> = {
  [ProductType.CARS]: carsConfig,
  [ProductType.ACTIVITIES]: activitiesConfig,
  [ProductType.LODGING]: lodgingConfig,
  [ProductType.FLIGHTS]: flightsConfig,
  [ProductType.PACKAGE]: packagesConfig,
  [ProductType.HOMES]: homesConfig,
};

export { COMPLETED_KEY };
